import style from "./InformazioniUtente.module.css";
import { FormLabel } from "../../components/form-label/FormLabel";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  nomeCognomeRegex,
  phoneRegex,
  required_error,
} from "../../services/shared";
import React, { useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { useAuth } from "../../providers/AuthProvider";
import {apiClient} from "../../services/api";
type FormData = z.infer<typeof formSchema>;
const formSchema = z.object({
  displayName: z
    .string({ required_error, invalid_type_error: "Formato non valido" })
    .min(2, "Il nome è obbligatorio")
    .regex(nomeCognomeRegex, "Il nome può contenere solo lettere e spazi"),
  email: z.string().email("Email non valida"),
  phoneNumber: z
    .string({ required_error, invalid_type_error: "Formato non valido" })
    .min(8, "Cellulare non valido")
    .regex(phoneRegex, "Numero di cellulare italiano non valido"),
  phonePrefix: z.enum(["+39", "+355"]),
});
export default function InformazioniUtente() {
  const { user, freshToken, refreshUser } = useAuth();
  const [snackState, setSnackState] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<{
    severity: any;
    message: string;
  }>({
    severity: "success",
    message: "",
  });
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema)
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;
  useEffect(() => {
    if (!user || !user?.phoneNumber || !user.email || !user.displayName) return;

    const phone = user.phoneNumber;
    const knownPrefixes = ["+39", "+355"];

    const matchedPrefix = knownPrefixes.find((prefix) =>
        phone.startsWith(prefix)
    );

    const numeroSenzaPrefisso = matchedPrefix
        ? phone.slice(matchedPrefix.length)
        : "";

    reset({
      displayName: user.displayName,
      phoneNumber: numeroSenzaPrefisso,
      email: user.email,
      phonePrefix: matchedPrefix === "+355" ? "+355" : "+39",
    });
  }, [user]);

  const onSubmit = handleSubmit(async (data): Promise<void> => {
    setSnackState(false);
    if (!user || !user.uid) {
      setSnackMessage({
        severity: "error",
        message: "User non è autenticato",
      });
      setSnackState(true);
      return;
    }
    let res = null;
    const phone = data.phoneNumber.toString().toLocaleLowerCase().trim();
    const payload = {
      uid: user?.uid as string,
      displayName: data.displayName.toString().toLocaleLowerCase().trim(),
      phoneNumber: `${data.phonePrefix}${phone}`,
      email: data.email.toString().toLocaleLowerCase().trim(),
    };
    const freshTK = await freshToken();
    try {
      res = await apiClient.post(`/v1/users/update/${payload.uid}`, payload, {
        headers: {
          "Content-Type": `application/json`,
        },
      });
    } catch (e) {
      console.error("errore", e);
      setSnackMessage({
        severity: "error",
        message: "Qualsosa è andato storto",
      });
      setSnackState(true);
    }
    if (res?.status && res?.data.ok === true) {
      setSnackMessage({ severity: "success", message: "Utente aggiornato" });
      setSnackState(true);
      await refreshUser();
      reset({
        displayName: payload.displayName,
        email: payload.email,
        phoneNumber: payload.phoneNumber.replace(/^\+39|\+355/, ""), // rimuove il prefisso
        phonePrefix: payload.phoneNumber.startsWith("+355") ? "+355" : "+39",
      });
    }
  });
  return (
    <div style={{ width: "100%" }}>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackMessage.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
      <FormLabel label={"Informazioni utente"}></FormLabel>
      <form
        onSubmit={onSubmit}
        style={{
          padding: "0 1em",
          margin: "auto",
          marginBottom: "1em",
          maxWidth: "100%",
          background: "white",
        }}
        className="flex-col gap-2"
      >
        <div className="flex-row gap-2">
          <Controller
            name="displayName"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Username"
                placeholder="Es. Massimo Rossi"
                value={field.value ?? ""}
                error={!!errors.displayName}
                helperText={errors.displayName?.message}
              />
            )}
          />
        </div>
        <div className="flex-row">
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                size="small"
                label="Email"
                value={field.value || ""}
                placeholder="Inserisci email"
                error={!!errors.email}
                helperText={errors.email?.message}
              />
            )}
          />
        </div>
        <div className="flex-row gap-2">
          <Controller
            name="phonePrefix"
            control={control}
            render={({ field }) => (
              <div style={{ width: "100%", maxWidth: "125px" }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="tipologia-label">Prefisso</InputLabel>
                  <Select
                    {...field}
                    style={{ textAlign: "left" }}
                    labelId="phone-prefix"
                    value={field.value || ""}
                    error={!!errors.phonePrefix}
                  >
                    <MenuItem value="" disabled>
                      Seleziona una opzione
                    </MenuItem>
                    <MenuItem value="+39">🇮🇹{" +39"}</MenuItem>
                    <MenuItem value="+355">🇦🇱{" +355"}</MenuItem>
                  </Select>
                  {errors.phonePrefix && (
                    <FormHelperText style={{ color: "#d32f2f" }}>
                      {errors.phonePrefix.message}
                    </FormHelperText>
                  )}
                </FormControl>
              </div>
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                size="small"
                label="Cellulare"
                value={field.value || ""}
                placeholder="Inserisci cellulare"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber?.message}
              />
            )}
          />
        </div>
        <Button variant={"contained"} type={"submit"} disabled={isSubmitting}>
          {isSubmitting ? "Elaboro richiesta" : "Salva"}
        </Button>
      </form>
    </div>
  );
}
